/**
 * "Wheel moves the playhead" (W3-05; UX-036, RPE NAV-02 / NAV-03, DESIGN.md §9.1 "NAV-01..13 Keep").
 *
 * In RPE the mouse wheel changes the global time: the editor scrolls and the preview follows. Here the timeline host
 * keeps doing the scrolling (its own wheel handling: sensitivity, Shift and the RPE preset's Alt fast scroll, the
 * scrollbar and header exceptions, a column's own wheel use such as the RPE Ctrl+wheel fine-tune) and this module
 * pins the playhead to the playhead line afterwards: when a plain, Shift or Alt wheel event moved the view, the
 * transport seeks to the beat now under the playhead line, so the preview shows that moment.
 *
 * The setting `ext.noteLane.wheelSeeks` ('preset' default = on in the RPE keymap preset only, 'on', 'off') is a row
 * on the Settings page (noteLane.module.tsx). Ctrl / Cmd wheels (zoom, fine-tune, line switching) never seek.
 */

import type { AppServices } from '../../app/registry/types';
import type { TimelineHostController } from '../../render/timeline/TimelineHost';
import { tempoOf } from './laneSession';

export const WHEEL_SEEK_EXT = 'noteLane.wheelSeeks';

export type WheelSeekSetting = 'preset' | 'on' | 'off';

/** The setting's value ('preset' for anything unknown). */
export function wheelSeekSetting(services: Pick<AppServices, 'settings'>): WheelSeekSetting {
  let v: unknown;
  try {
    v = services.settings.getExt(WHEEL_SEEK_EXT);
  } catch {
    v = undefined;
  }
  return v === 'on' || v === 'off' ? v : 'preset';
}

/** True when the wheel moves the playhead now (the setting, or the RPE preset when it follows the preset). */
export function wheelSeeks(services: Pick<AppServices, 'settings' | 'keymap'>): boolean {
  const v = wheelSeekSetting(services);
  if (v !== 'preset') return v === 'on';
  return services.keymap.preset === 'rpe';
}

function editableTarget(t: EventTarget | null): boolean {
  if (typeof HTMLElement === 'undefined' || !(t instanceof HTMLElement)) return false;
  return t.isContentEditable || t.closest('input, textarea, select, [contenteditable="true"]') !== null;
}

/**
 * Listens on the host's root: the capture phase notes the view's bottom beat before the host handles the wheel,
 * the bubble phase (after the host's own listener) seeks when the view moved. Returns the disposer.
 */
export function installWheelSeek(
  services: AppServices,
  host: Pick<TimelineHostController, 'root' | 'axis' | 'view' | 'playing' | 'resumeFollow'>,
  onSeek: () => void,
): () => void {
  const root = host.root;
  if (root === null) return () => undefined;
  let before: number | null = null;
  const capture = (e: WheelEvent): void => {
    before = null;
    if (e.ctrlKey || e.metaKey || editableTarget(e.target) || !wheelSeeks(services)) return;
    before = host.axis.bottomBeat;
  };
  const after = (): void => {
    const b0 = before;
    before = null;
    if (b0 === null) return;
    const b1 = host.axis.bottomBeat;
    if (b1 === b0) return;
    const tempo = tempoOf(services);
    if (tempo === null) return;
    const beat = b1 + host.view().playheadRatio * host.axis.span;
    services.audio.transport.seek(Math.max(0, tempo.secAtBeatF64(Math.max(0, beat))));
    // While playing the view keeps following the (moved) playhead.
    if (host.playing) host.resumeFollow();
    onSeek();
  };
  root.addEventListener('wheel', capture, { capture: true, passive: true });
  root.addEventListener('wheel', after, { passive: true });
  return () => {
    root.removeEventListener('wheel', capture, { capture: true });
    root.removeEventListener('wheel', after);
  };
}
